import type { Placement } from '@vielzeug/orbit';
import {
  bind,
  createStableId,
  define,
  getHost,
  html,
  onCleanup,
  onMounted,
  prop,
  useEmit,
  useSlots,
} from '@vielzeug/ore';
import { computed } from '@vielzeug/ripple';

import { type OverlayOpenChangeDetail, parseStringTriggers } from '../../core';
import { disablableBundle } from '../../shared';
import { reducedMotionMixin } from '../../styles';
import { useFloatingTrigger } from '../shared/use-floating-trigger';
import styles from './popover.css?inline';

export type PopoverTrigger = 'click' | 'focus' | 'hover';

const PANEL_OFFSET = 8;
const VALID_TRIGGERS = new Set<PopoverTrigger>(['click', 'focus', 'hover']);
const DEFAULT_POPOVER_TRIGGERS: PopoverTrigger[] = ['click'];

const parseOptionalBool = (value: string | null): boolean | undefined =>
  value == null ? undefined : value === '' || value === 'true';

const normalizeTriggers = (value: unknown): PopoverTrigger[] =>
  parseStringTriggers(String(value ?? ''), VALID_TRIGGERS, DEFAULT_POPOVER_TRIGGERS);

export type OrePopoverEvents = {
  'open-change': OverlayOpenChangeDetail;
};

/** Popover component properties */
export type OrePopoverProps = {
  /** Initial uncontrolled open state. Ignored when `open` is set. */
  'default-open'?: boolean;
  /** Disable the popover */
  disabled?: boolean;
  /** Accessible label for the panel */
  label?: string;
  /** Gap between trigger and panel in px */
  offset?: number;
  /** Controlled open state */
  open?: boolean;
  /** Preferred placement relative to the trigger */
  placement?: Placement;
  /** Which trigger(s) open/close the popover */
  trigger?: string;
};

/**
 * A floating panel anchored to a trigger element.
 *
 * @element ore-popover
 * @attr {string} placement - Preferred placement (default: 'bottom')
 * @attr {string} trigger - 'click' | 'hover' | 'focus' (default: 'click')
 * @attr {boolean} open - Controlled open state
 * @attr {boolean} default-open - Initial uncontrolled open state
 * @attr {number} offset - Gap in px (default: 8)
 * @attr {boolean} disabled - Disables the popover
 * @attr {string} label - aria-label on the panel
 * @fires open-change - When the panel state changes. detail: { open, reason }
 * @slot - The trigger element
 * @slot content - Panel content
 * @part panel - Panel container.
 *
 * @example
 * ```html
 * <ore-popover placement="bottom" trigger="click">
 *   <ore-button>Open info</ore-button>
 *   <div slot="content">
 *     <p>Popover body content goes here.</p>
 *   </div>
 * </ore-popover>
 * ```
 */
export const POPOVER_TAG = 'ore-popover' as const;
define<OrePopoverProps>(POPOVER_TAG, {
  props: {
    ...disablableBundle,
    'default-open': prop.bool(false),
    label: prop.string(),
    offset: prop.number(PANEL_OFFSET),
    open: { default: undefined as boolean | undefined, parse: parseOptionalBool },
    placement: prop.oneOf(
      [
        'bottom',
        'bottom-end',
        'bottom-start',
        'left',
        'left-end',
        'left-start',
        'right',
        'right-end',
        'right-start',
        'top',
        'top-end',
        'top-start',
      ] as const,
      'bottom',
    ),
    trigger: prop.string('click'),
  },
  setup(props) {
    const el = getHost();
    const emit = useEmit<OrePopoverEvents>();
    const slots = useSlots();

    const shadowRoot = el.shadowRoot;
    const isDisabled = computed(() => Boolean(props.disabled.value));
    const panelId = createStableId('popover');
    const triggers = computed<PopoverTrigger[]>(() => normalizeTriggers(props.trigger.value));
    let panelEl: HTMLElement | null = null;

    const floating = useFloatingTrigger({
      bindTriggerAria: (triggerEl) =>
        bind(
          {
            aria: {
              controls: () => panelId,
              disabled: () => String(isDisabled.value),
              expanded: () => String(floating.visible.value),
              haspopup: 'dialog',
            },
          },
          { target: triggerEl },
        ),
      defaultOpen: props['default-open'],
      disabled: isDisabled,
      getHost: () => el,
      getPanel: () => panelEl,
      offset: props.offset,
      onCleanup,
      onClose: (reason) => emit('open-change', { open: false, reason }),
      onOpen: (reason) => emit('open-change', { open: true, reason }),
      openProp: props.open as typeof props.open & { value: boolean | undefined },
      placement: computed(() => props.placement.value as Placement),
      slot: () => shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])') ?? null,
      slotElements: slots.elements(),
      triggers,
    });

    onMounted(() => floating.mount());

    return html`
      <slot></slot>
      <div
        class="panel"
        part="panel"
        id="${panelId}"
        role="dialog"
        aria-modal="false"
        popover="manual"
        aria-label="${props.label}"
        aria-hidden="${() => String(!floating.visible.value)}"
        ref=${(ref: HTMLElement) => {
          panelEl = ref;
        }}>
        <slot name="content"></slot>
      </div>
    `;
  },
  styles: [reducedMotionMixin, styles],
});
